import React, { useEffect, useState } from "react";
import styles from "./PromptAction.module.css";
import Button from "../Button/Button";
import ReactDOM from "react-dom";

type PromptActionProps = {
  isVisible: boolean;
  onClose: () => void;
  clickHandler: (value: string) => void | Promise<void>;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  defaultValue?: string;
  requiredMessage?: string;
  icon?: string;
  severity?: "warning" | "danger";
  loading?: boolean;
};

const PromptAction = ({
  isVisible,
  onClose,
  clickHandler,
  title = "Enter a value",
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  placeholder = "",
  defaultValue = "",
  requiredMessage = "This field is required",
  icon,
  severity = "warning",
  loading = false,
}: PromptActionProps) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setValue(defaultValue);
      setError("");
      setIsProcessing(false);
    }
  }, [isVisible, defaultValue]);

  const handleConfirm = async () => {
    if (loading || isProcessing) return;

    const trimmed = value.trim();
    if (!trimmed) {
      setError(requiredMessage);
      return;
    }

    setIsProcessing(true);
    try {
      await clickHandler(trimmed);
      onClose();
    } catch (err) {
      console.error("Prompt action failed:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (loading || isProcessing) return;
    onClose();
  };

  const isDisabled = loading || isProcessing;

  if (!isVisible) return null;

  const popupElement = document.getElementById("popup");

  if (!popupElement) {
    console.error("Popup element not found!");
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          {icon && (
            <img
              src={icon}
              alt=""
              className={`${styles.icon} ${styles[`icon_${severity}`]}`}
            />
          )}
          {title && <h3 className={styles.title}>{title}</h3>}
        </div>

        {message && (
          <div className={styles.message}>
            <p>{message}</p>
          </div>
        )}

        <input
          className={`${styles.inputField} ${error ? styles.inputFieldError : ""}`}
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={isDisabled}
          autoFocus
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleConfirm();
            }
          }}
        />
        {error && <p className={styles.inputError}>{error}</p>}

        <div className={styles.actions}>
          <Button
            clickHandler={handleCancel}
            disabled={isDisabled}
            colour="#6c757d"
            textColour="#fff"
            size="Small"
          >
            {cancelText}
          </Button>

          <Button
            clickHandler={handleConfirm}
            disabled={isDisabled}
            colour={severity === "danger" ? "#dc3545" : "#17a2b8"}
            textColour="#fff"
            size="Small"
          >
            {loading || isProcessing ? "Processing..." : confirmText}
          </Button>
        </div>
      </div>
    </div>,
    popupElement
  );
};

export default PromptAction;
